import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArticleCardHero, ArticleCard, ArticleCardList } from "@/components/ArticleCard";
import AdSlot from "@/components/AdSlot";
import Newsletter from "@/components/Newsletter";
import SEO from "@/components/SEO";
import { ArrowRight } from "@phosphor-icons/react";

const CATS = [
  { slug: "investing", name: "Investing" },
  { slug: "personal-finance", name: "Personal Finance" },
  { slug: "cryptocurrency", name: "Cryptocurrency" },
  { slug: "stock-market", name: "Stock Market" },
  { slug: "retirement-planning", name: "Retirement Planning" },
  { slug: "market-analysis", name: "Market Analysis" },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    api.featured(5).then(setFeatured).catch(() => {});
    api.articles({ limit: 9 }).then(setLatest).catch(() => {});
    api.trending(5).then(setTrending).catch(() => {});
  }, []);

  const hero = featured[0];
  const secondaries = featured.slice(1, 3);

  return (
    <>
      <SEO
        title=""
        description="Independent, editorial-grade coverage of investing, personal finance, cryptocurrency, and markets."
        url="/"
      />

      {/* Masthead / Date bar */}
      <div className="border-b border-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs font-mono text-foreground/60">
          <span data-testid="masthead-date">
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </span>
          <span className="hidden sm:inline uppercase tracking-widest">Vol. I · Independent Finance Journalism</span>
        </div>
      </div>

      {/* Hero bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid gap-6 md:grid-cols-12">
          <div className="md:col-span-8">
            {hero && <ArticleCardHero a={hero} />}
          </div>
          <div className="md:col-span-4 grid gap-6">
            {secondaries.map((a) => (
              <ArticleCard key={a.id} a={a} />
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdSlot size="leaderboard" />
      </div>

      {/* Latest + sidebar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-8">
          <div className="flex items-center justify-between border-b-2 border-foreground pb-3 mb-6">
            <h2 className="font-display text-3xl font-black">Latest</h2>
            <Link to="/category/investing" className="text-sm font-medium inline-flex items-center gap-1 hover:text-primary">
              Explore all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {latest.slice(0, 6).map((a) => (
              <ArticleCard key={a.id} a={a} />
            ))}
          </div>
        </div>

        <aside className="md:col-span-4 space-y-8">
          <div>
            <div className="border-b-2 border-foreground pb-3 mb-4">
              <h3 className="font-display text-2xl font-black">Most Read</h3>
            </div>
            <div>
              {trending.map((a, i) => (
                <div key={a.id} className="flex gap-3 py-3 border-b border-foreground/10">
                  <span className="font-display font-black text-3xl text-foreground/25 leading-none w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Link
                    to={`/article/${a.slug}`}
                    data-testid={`trending-${a.slug}`}
                    className="font-display font-semibold leading-snug text-sm hover:text-primary transition-colors duration-200"
                  >
                    {a.title}
                  </Link>
                </div>
              ))}
            </div>
          </div>
          <AdSlot size="sidebar" />
        </aside>
      </section>

      {/* Categories row */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="border-y-2 border-foreground py-8">
          <div className="overline mb-3">Explore the desks</div>
          <h2 className="font-display text-4xl md:text-5xl font-black">Six categories. One editorial standard.</h2>
        </div>
        <div className="grid gap-0 md:grid-cols-3 mt-8 border border-foreground/15">
          {CATS.map((c, i) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`home-cat-${c.slug}`}
              className={`p-8 hover:bg-primary hover:text-primary-foreground transition-colors duration-200 group border-foreground/15 ${
                i % 3 !== 2 ? "md:border-r" : ""
              } ${i < 3 ? "md:border-b" : ""} border-b md:border-b`}
            >
              <div className="overline group-hover:text-primary-foreground/70">Desk 0{i + 1}</div>
              <h3 className="font-display text-2xl font-bold mt-3">{c.name}</h3>
              <p className="text-sm mt-2 opacity-80">Read the latest analysis and guides.</p>
              <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium">
                Enter <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Newsletter />

      {/* More stories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="border-b-2 border-foreground pb-3 mb-6">
          <h2 className="font-display text-3xl font-black">More stories</h2>
        </div>
        <div className="grid gap-0 md:grid-cols-2">
          {latest.slice(6).map((a) => (
            <ArticleCardList key={a.id} a={a} />
          ))}
        </div>
      </section>
    </>
  );
}
